import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { translate } from "shared/languages";
import { getApiErrorMessage, getApiSuccessMessage } from "shared/utils";
import { windowConflictOf } from "shared/utils/windowConflict";
import { WindowConflict } from "shared/types";
import {
  AddToWaitlistForm,
  addToWaitlistV2,
  removeFromWaitlistV2,
  reorderWaitlistV2,
} from "hooks/api/waitlist-v2";

const invalidateForSlot = (
  queryClient: ReturnType<typeof useQueryClient>,
  schedule_id?: string,
  occurrence_timestamp?: string,
) => {
  queryClient.invalidateQueries({
    queryKey: ["waitlist-v2", "schedule", schedule_id, occurrence_timestamp],
  });
  queryClient.invalidateQueries({
    queryKey: ["booking-v2", "schedule", schedule_id, occurrence_timestamp],
  });
};

// onWindowConflict: see useBookOpenClassV2.
export const useAddToWaitlistV2 = (
  onWindowConflict?: (conflict: WindowConflict, form: AddToWaitlistForm) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: AddToWaitlistForm) => addToWaitlistV2(form),
    onSuccess: (response, variables) => {
      invalidateForSlot(
        queryClient,
        variables.schedule_id,
        variables.occurrence_timestamp,
      );
      if (variables.on_conflict) {
        queryClient.invalidateQueries({ queryKey: ["booking-v2"] });
        queryClient.invalidateQueries({ queryKey: ["waitlist-v2"] });
        queryClient.invalidateQueries({ queryKey: ["customer_package"] });
      }
      getApiSuccessMessage(`Waitlist ${translate("messages.success.update")}`);
    },
    onError: (error: AxiosError<ErrorResponse>, form) => {
      const conflict = windowConflictOf(error);
      if (conflict && onWindowConflict && !form.on_conflict) {
        onWindowConflict(conflict, form);
        return;
      }
      getApiErrorMessage(error);
    },
  });
};

export const useRemoveFromWaitlistV2 = (
  schedule_id?: string,
  occurrence_timestamp?: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingId: string) => removeFromWaitlistV2(bookingId),
    onSuccess: () => {
      invalidateForSlot(queryClient, schedule_id, occurrence_timestamp);
      getApiSuccessMessage(`Waitlist ${translate("messages.success.update")}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export const useReorderWaitlistV2 = (
  schedule_id?: string,
  occurrence_timestamp?: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      newPosition,
    }: {
      bookingId: string;
      newPosition: number;
    }) => reorderWaitlistV2(bookingId, newPosition),
    onSuccess: () => {
      invalidateForSlot(queryClient, schedule_id, occurrence_timestamp);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};
